import React, {useContext, useEffect, useState} from 'react';
import {useLocation, useNavigate, useParams} from 'react-router-dom';

import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import BackArrow from '@mui/icons-material/ArrowBack';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid2';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

import {convertValue} from '../../helpers/salary';
import RootContext from '../../services/context-states/root-context';
import {assessmentApi} from '../../api/openapi-axios-client';
import {HorizontalScrollBox, Loading} from '../../elements';
import {fastGrowingCareer, truncateString} from '../../helpers/strings';
import { RootContextType, CareerClusterDetails, ClusterDisplayData, VideoHistoryRating, ONetCareerDetails } from '../../types';
import { callApi } from '../../api/helpers';
import { getClusterImage } from '../../helpers/images';

interface LocationState {
    clusterData?: ClusterDisplayData;
}

const ClusterDetailsPage: React.FC = () => {
    const {messageContext} = useContext<RootContextType>(RootContext);
    const {setError} = messageContext;

    const [loading, setLoading] = useState<boolean>(true);
    const [occupationDetails, setOccupationDetails] = useState<CareerClusterDetails[]>([]);
    const [clusterDisplayData, setClusterDisplayData] = useState<ClusterDisplayData | null>(null);

    const navigate = useNavigate();
    const location = useLocation();
    const state = location.state as LocationState;
    const {clusterData} = state || {};
    const {userId, careerClusterId, clusterCode} = useParams();

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

    useEffect(() => {
        const getDetail = async () => {
            let dataForDisplay: ClusterDisplayData | null = null;

            if(!clusterData) {
                const clusters = await callApi<VideoHistoryRating[]>(() => assessmentApi.ratings.getUserHistoryRating(userId || ''));

                if(!clusters) {
                    setError('There was an error retrieving the cluster data.');
                    setLoading(false);
                    return;
                }

                const matchedCluster = clusters.find(cluster => cluster.categoryId === careerClusterId);

                if(!matchedCluster) {
                    setError('There was an error retrieving the cluster data.');
                    setLoading(false);
                    return;
                }

                dataForDisplay = {
                    categoryId: matchedCluster.categoryId,
                    category: matchedCluster.category,
                    averageRating: matchedCluster.averageRating,
                    categoryDescription: matchedCluster.categoryDescription,
                    occupationData: matchedCluster.occupationData
                };

                setClusterDisplayData(dataForDisplay);
            }
            else {
                dataForDisplay = {
                    categoryId: clusterData.categoryId,
                    category: clusterData.category,
                    averageRating: clusterData.averageRating,
                    categoryDescription: clusterData.categoryDescription,
                    occupationData: clusterData.occupationData
                };

                setClusterDisplayData(dataForDisplay);
            }

            if(dataForDisplay && dataForDisplay.occupationData) {
                const occupationData: CareerClusterDetails[] = [];

                try {
                    for(const occupation of dataForDisplay.occupationData) {
                        const response = await callApi<ONetCareerDetails>(() => assessmentApi.careerData.getONetCareerData(occupation.code));
    
                        if(response && response.code) {
                            occupationData.push({
                                ...occupation,
                                ...response,
                                details: response
                            });
                        }
                    }
  
                    setOccupationDetails(occupationData.sort((a, b) => b.averageRating - a.averageRating));
                }
                catch(err) {
                    setError('Unable to load career details');
                    setOccupationDetails([]);
                }
            }

            setLoading(false);
        };

        getDetail();
    }, [userId, careerClusterId, clusterData, setError]);

    const generateCareerCard = (categoryId: string, category: string, occupation: CareerClusterDetails, i: number) => {
        return (
            <Box
                key={i}
                sx={{
                    borderRadius: 1,
                    border: .5,
                    borderColor: 'container.border',
                    padding: 1,
                    bgcolor: 'container.background',
                    width: { xs: 280, sm: 300 },
                    height: { xs: 180, sm: 200 },
                    margin: 1,
                    boxShadow: 4,
                    cursor: 'pointer'
                }}
                onClick={() => navigate(`/careers/details/${userId}/${careerClusterId}/career/${careerClusterId}-${occupation.occupationId}-${occupation.occupationId}`, {state: {careerData: {backLabel: 'Top Interests', categoryId, category, occupation}}})}>
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    padding: { xs: 1, sm: 1.25 },
                    gap: { xs: 1, sm: 1.25 },
                    justifyContent: 'center'
                }}>
                    <Typography sx={{
                        fontSize: { xs: 14, sm: 16 },
                        fontWeight: 900,
                        color: 'text.primary'
                    }}>
                        {truncateString(occupation.title, isMobile ? 40 : 50)}
                    </Typography>
                    {occupation?.details?.tags?.bright_outlook && (
                        <Typography sx={{
                            fontSize: { xs: 12, sm: 14 },
                            color: 'text.fastCareer'
                        }}>
                            {`${fastGrowingCareer} Fast-growing career`}
                        </Typography>)
                    }
                    <Typography sx={{
                        fontSize: { xs: 12, sm: 14 },
                        color: 'primary.main'
                    }}>
                        {`${convertValue(occupation?.averageRating)}% match`}
                    </Typography>
                    <Box sx={{display: 'flex', flexDirection: 'row', alignItems: 'center'}}>
                        <Typography sx={{
                            fontSize: { xs: 12, sm: 14 },
                            color: 'grey.500'
                        }}>
                            See more
                        </Typography>
                        <ArrowRightAltIcon sx={{color: 'grey.500', fontSize: { xs: 18, sm: 20 }}}/>
                    </Box>
                </Box>
            </Box>
        );
    };

    return (
        <>
        {(loading && <Loading />) ||
            <Container
                maxWidth={isMobile ? false : "lg"}
                disableGutters={isMobile}
                sx={{
                    py: { xs: 2, sm: 3, md: 4 },
                    px: { xs: 2, sm: 3, md: 3 },
                    width: '100%'
                }}>
                <Box sx={{ mb: { xs: 2, sm: 3 } }}>
                    <Stack direction='row' spacing={0} display={'flex'} alignItems={'center'}>
                        <IconButton onClick={() => navigate('/results')}>
                            <BackArrow sx={{
                                color: 'text.secondary',
                                fontSize: { xs: '2rem', sm: '2.5rem' }
                            }}/>
                        </IconButton>
                        <Typography sx={{
                            fontSize: { xs: 16, sm: 20 },
                            color: 'text.secondary',
                            verticalAlign:'center',
                            cursor:'pointer'
                        }} onClick={() => navigate('/results')}>
                            {isMobile ? 'Back' : 'Return to Top Interests'}
                        </Typography>
                    </Stack>
                </Box>

                <Box sx={{
                    borderRadius: 2,
                    border: 1,
                    borderColor: 'container.border',
                    bgcolor: 'container.background',
                    display:'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    overflow: 'hidden'
                }}>
                    <Box
                        component="img"
                        src={getClusterImage(`${clusterCode}.0000`)}
                        alt={`Cluster illustration for ${clusterDisplayData?.category ?? 'career cluster'}`}
                        sx={{
                            width: { xs: '100%', md: 400 },
                            height: { xs: 200, sm: 250, md: 'auto' },
                            objectFit: 'cover'
                        }}
                    />
                    <Box sx={{
                        padding: { xs: 2, sm: 3, md: 4 },
                        flex: 1
                    }}>
                        <Typography sx={{
                            fontSize: { xs: 18, sm: 20, md: 22 },
                            fontWeight: 900,
                            color: 'text.primary',
                            mb: 2
                        }}>
                            {clusterDisplayData?.category}
                        </Typography>
                        <Typography sx={{
                            fontSize: { xs: 14, sm: 16 },
                            color: 'text.primary',
                            mb: 2
                        }}>
                            {`You had a ${convertValue(clusterDisplayData?.averageRating || 0)}% match with this cluster.`}
                        </Typography>
                        <Typography sx={{
                            fontSize: { xs: 14, sm: 16 },
                            color: 'text.primary'
                        }}>
                            {clusterDisplayData?.categoryDescription}
                        </Typography>
                    </Box>
                </Box>

                <Box sx={{ mt: { xs: 3, sm: 4 } }}>
                    <Divider sx={{color: 'divider', background: 'divider'}}/>
                    <Typography sx={{
                        fontSize: { xs: 18, sm: 20, md: 22 },
                        fontWeight: 900,
                        color: 'text.primary',
                        mt: { xs: 3, sm: 4 },
                        mb: 1
                    }}>
                        Potential Career Paths
                    </Typography>
                    <Typography sx={{
                        fontSize: { xs: 12, sm: 14 },
                        color: 'text.primary',
                        mb: { xs: 2, sm: 3 }
                    }}>
                        Career paths that can be found in these top categories.
                    </Typography>
                    <HorizontalScrollBox toRender={occupationDetails.map((occupation, i) => generateCareerCard(clusterDisplayData?.categoryId || '', clusterDisplayData?.category || '', occupation, i))} />
                </Box>
            </Container>
        }
        </>
    );
};

export default ClusterDetailsPage; 